import React, { useState, useEffect } from 'react';
import { theme, alpha } from '../../theme';

interface EligibleGroup {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  groups: EligibleGroup[];
  currentGroupId: string | null;
  onSave: (groupId: string | null) => Promise<{ ok: boolean; error?: string }>;
}

export function MatchGroupPickerModal({ open, onClose, groups, currentGroupId, onSave }: Props) {
  const [selected, setSelected] = useState<string | null>(currentGroupId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(currentGroupId);
      setError(null);
    }
  }, [currentGroupId, open]);

  if (!open) return null;

  const disabled = saving || selected === currentGroupId;

  async function handleSave() {
    setSaving(true);
    setError(null);
    const result = await onSave(selected);
    setSaving(false);
    if (result.ok) {
      onClose();
    } else {
      setError(result.error || 'Error al guardar');
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !saving) onClose();
  };

  const renderOption = (id: string | null, label: string) => {
    const checked = selected === id;
    return (
      <label
        key={id ?? '__none__'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.sm,
          padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
          borderRadius: theme.borderRadius.input,
          cursor: 'pointer',
          backgroundColor: checked ? `${alpha('primary', '22')}` : 'transparent',
          border: `1px solid ${checked ? theme.colors.primary : theme.colors.border}`,
          marginBottom: theme.spacing.xs,
        }}
      >
        <input
          type="radio"
          name="match-group-picker"
          value={id ?? ''}
          checked={checked}
          onChange={() => setSelected(id)}
          style={{ accentColor: theme.colors.primary }}
        />
        <span
          style={{
            color: theme.colors.text,
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeBody,
          }}
        >
          {label}
        </span>
      </label>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          padding: theme.spacing.lg,
          maxWidth: '420px',
          width: '90%',
          boxShadow: theme.colors.shadow,
          border: `2px solid ${theme.colors.primary}`,
        }}
      >
        <h3
          style={{
            margin: `0 0 ${theme.spacing.md} 0`,
            fontSize: theme.fonts.sizeSubtitle,
            fontWeight: theme.fonts.weightBold,
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
          }}
        >
          Asociar a un grupo
        </h3>

        <div style={{ marginBottom: theme.spacing.md, maxHeight: '300px', overflowY: 'auto' }}>
          {renderOption(null, 'Sin grupo')}
          {groups.map(g => renderOption(g.id, g.name))}
        </div>

        {error && (
          <p
            style={{
              margin: `0 0 ${theme.spacing.sm} 0`,
              fontSize: theme.fonts.sizeSmall,
              color: theme.colors.danger,
              fontFamily: theme.fonts.body,
            }}
          >
            {error}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            gap: theme.spacing.sm,
            justifyContent: 'flex-end',
            marginTop: theme.spacing.md,
          }}
        >
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              backgroundColor: theme.colors.secondary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              fontWeight: theme.fonts.weightBold,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={disabled}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              backgroundColor: theme.colors.primary,
              color: theme.colors.background,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              fontWeight: theme.fonts.weightBold,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
